import { useState } from "react";
import { Pane } from "react-leaflet";
import { getAllGeometryAssets, updateShapeAnnotation } from "../app/networkSlice";
import Asset from "./assets/Asset";
import { useSelector } from "react-redux";
import {
  findAll,
  updateConnectionPoint,
  updateGroupedConnection,
  updateMotor,
  updateNode,
  updatePointOfConnection,
  updateTransformer,
  updateWelder,
  updateStickyNote,
} from "../app/networkSlice";
import ResultMarker from "./ResultMarker";
import { ZIndex } from "../constants/zindex";

const Network = ({
  networkId,
  handleSelect,
  clickedAsset,
  snapTargetAssetId,
  handleMove,
  reshapeCablePoints,
  renderer,
  fallbackRenderer,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const groupedConnections = useSelector((state) => state.network.present.groupedConnections);
  const allGeometryAssets = useSelector((state) => getAllGeometryAssets(state));

  const isNode = (groupedConnection) => {
    return !groupedConnection.groupedConnectionPoints.length && !groupedConnection.linkBox;
  };

  return (
    <>
      <Pane name="leafletTooltipPane" style={{ zIndex: ZIndex.networkTooltip }} />
      <Pane name="network-transformers" style={{ zIndex: ZIndex.networkTransformers }}>
        {useSelector((state) => state.network.present.transformers).map((transformer) => (
          <Asset
            key={transformer.id}
            networkId={networkId}
            asset={transformer}
            onClick={handleSelect}
            onMove={(e, a) => handleMove(e, a, updateTransformer)}
            highlighted={clickedAsset && transformer.id === clickedAsset.id}
            reshapeCablePoints={reshapeCablePoints}
            isDragging={isDragging}
            setIsDragging={(value) => setIsDragging(value)}
            allGeometryAssets={allGeometryAssets}
          />
        ))}
      </Pane>
      <Pane name="network-connection-points" style={{ zIndex: ZIndex.networkConnectionPoints }}>
        {useSelector((state) => state.network.present.connectionPoints).map((connectionPoint) => (
          <Asset
            key={connectionPoint.id}
            networkId={networkId}
            asset={connectionPoint}
            onClick={handleSelect}
            onMove={(e, a) => handleMove(e, a, updateConnectionPoint)}
            highlighted={clickedAsset && connectionPoint.id === clickedAsset.id}
            reshapeCablePoints={reshapeCablePoints}
            isDragging={isDragging}
            setIsDragging={(value) => setIsDragging(value)}
            allGeometryAssets={allGeometryAssets}
          />
        ))}
        {useSelector((state) => state.network.present.motors).map((motor) => (
          <Asset
            key={motor.id}
            networkId={networkId}
            asset={motor}
            onClick={handleSelect}
            onMove={(e, a) => handleMove(e, a, updateMotor)}
            highlighted={clickedAsset && motor.id === clickedAsset.id}
            reshapeCablePoints={reshapeCablePoints}
            isDragging={isDragging}
            setIsDragging={(value) => setIsDragging(value)}
            allGeometryAssets={allGeometryAssets}
          />
        ))}
        {useSelector((state) => state.network.present.welders).map((welder) => (
          <Asset
            key={welder.id}
            networkId={networkId}
            asset={welder}
            onClick={handleSelect}
            onMove={(e, a) => handleMove(e, a, updateWelder)}
            highlighted={clickedAsset && welder.id === clickedAsset.id}
            reshapeCablePoints={reshapeCablePoints}
            isDragging={isDragging}
            setIsDragging={(value) => setIsDragging(value)}
            allGeometryAssets={allGeometryAssets}
          />
        ))}
        {useSelector((state) => state.network.present.pointOfConnections).map(
          (pointOfConnection) => (
            <Asset
              key={pointOfConnection.id}
              networkId={networkId}
              asset={pointOfConnection}
              onClick={handleSelect}
              onMove={(e, a) => handleMove(e, a, updatePointOfConnection)}
              highlighted={clickedAsset && pointOfConnection.id === clickedAsset.id}
              reshapeCablePoints={reshapeCablePoints}
              isDragging={isDragging}
              setIsDragging={(value) => setIsDragging(value)}
              allGeometryAssets={allGeometryAssets}
            />
          ),
        )}
        {groupedConnections
          .filter((gc) => !isNode(gc))
          .map((groupedConnection) => (
            <Asset
              key={groupedConnection.id}
              label={groupedConnection.count}
              networkId={networkId}
              asset={groupedConnection}
              onClick={handleSelect}
              onMove={(e, a) => handleMove(e, a, updateGroupedConnection)}
              highlighted={clickedAsset && groupedConnection.id === clickedAsset.id}
              reshapeCablePoints={reshapeCablePoints}
              isDragging={isDragging}
              setIsDragging={(value) => setIsDragging(value)}
              allGeometryAssets={allGeometryAssets}
            />
          ))}
      </Pane>
      <Pane name="network-nodes" style={{ zIndex: ZIndex.networkNodes }}>
        {groupedConnections
          .filter((gc) => isNode(gc))
          .map((groupedConnection) => (
            <Asset
              key={groupedConnection.id}
              networkId={networkId}
              asset={groupedConnection}
              onClick={handleSelect}
              onMove={(e, a) => handleMove(e, a, updateGroupedConnection)}
              highlighted={clickedAsset && groupedConnection.id === clickedAsset.id}
              reshapeCablePoints={reshapeCablePoints}
              isDragging={isDragging}
              setIsDragging={(value) => setIsDragging(value)}
              allGeometryAssets={allGeometryAssets}
            />
          ))}
        {useSelector((state) => state.network.present.nodes).map((node) => (
          <Asset
            key={node.id}
            networkId={networkId}
            asset={node}
            onClick={handleSelect}
            onMove={(e, a) => handleMove(e, a, updateNode)}
            highlighted={clickedAsset && node.id === clickedAsset.id}
            reshapeCablePoints={reshapeCablePoints}
            isDragging={isDragging}
            setIsDragging={(value) => setIsDragging(value)}
            allGeometryAssets={allGeometryAssets}
          />
        ))}
      </Pane>
      <div name="network-cables" style={{ zIndex: ZIndex.networkCables }}>
        {useSelector((state) => state.network.present.cables).map((cable) => (
          <Asset
            key={cable.id}
            networkId={networkId}
            asset={cable}
            onClick={handleSelect}
            highlighted={clickedAsset && cable.id === clickedAsset.id}
            reshapeCablePoints={reshapeCablePoints}
            isDragging={isDragging}
            setIsDragging={(value) => setIsDragging(value)}
            renderer={renderer}
            fallbackRenderer={fallbackRenderer}
            allGeometryAssets={allGeometryAssets}
          />
        ))}
      </div>
      <Pane name="network-results" style={{ zIndex: ZIndex.networkResults }}>
        {useSelector((state) => findAll(state)).map((asset) => (
          <ResultMarker
            key={asset.asset.id}
            asset={asset.asset}
            onClick={handleSelect}
            isDragging={isDragging}
          />
        ))}
      </Pane>
      <Pane name="annotations-pane" style={{ zIndex: ZIndex.networkAnnotations }}>
        {useSelector((state) => state.network.present.stickyNotes).map((stickyNote) => (
          <Asset
            key={stickyNote.id}
            networkId={networkId}
            asset={stickyNote}
            onClick={handleSelect}
            onMove={(e, a) => handleMove(e, a, updateStickyNote)}
            highlighted={
              (clickedAsset && stickyNote.id === clickedAsset.id) ||
              stickyNote.id === snapTargetAssetId
            }
            reshapeCablePoints={reshapeCablePoints}
            isDragging={isDragging}
            setIsDragging={(value) => setIsDragging(value)}
            allGeometryAssets={allGeometryAssets}
          />
        ))}
        {useSelector((state) => state.network.present.shapeAnnotations).map((shape) => (
          <Asset
            key={shape.id}
            networkId={networkId}
            asset={shape}
            onClick={handleSelect}
            onMove={(e, a) => handleMove(e, a, updateShapeAnnotation)}
            highlighted={
              (clickedAsset && shape.id === clickedAsset.id) || shape.id === snapTargetAssetId
            }
            reshapeCablePoints={reshapeCablePoints}
            isDragging={isDragging}
            setIsDragging={(value) => setIsDragging(value)}
            allGeometryAssets={allGeometryAssets}
          />
        ))}
      </Pane>
    </>
  );
};

export default Network;
